import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Sliders, Save, Loader2, AlarmClockOff, Percent, Calculator, Power } from 'lucide-react';

export default function RewardSettingsPage({ user, onLogout }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/reward-settings');
        setS(res.data);
      } catch (err) { toast.error('Could not load settings'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        reward_earning_percentage: parseFloat(s.reward_earning_percentage),
        point_expiry_months: parseInt(s.point_expiry_months, 10),
        rounding_rule: s.rounding_rule,
        reward_system_active: !!s.reward_system_active,
        manual_adjustment_allowed: !!s.manual_adjustment_allowed,
      };
      const res = await api.put('/admin/reward-settings', payload);
      setS(res.data);
      toast.success('Reward settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const runExpiryNow = async () => {
    if (!window.confirm('Run reward-points expiry sweep now?')) return;
    try {
      const res = await api.post('/admin/rewards/run-expiry-now');
      toast.success(`Expired ${res.data.batches_expired} batches, ${res.data.points_expired} points`);
    } catch (err) { toast.error('Expiry run failed'); }
  };

  if (loading || !s) return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
    </Layout>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <header>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sliders className="w-6 h-6 text-amber-600" />
            Reward Settings
          </h1>
          <p className="text-slate-500 text-sm mt-1">Control how Eden Reward Points are earned, expired and adjusted.</p>
        </header>

        <Card className="p-5 rounded-2xl border-slate-200 space-y-5">
          <Row icon={<Power className="w-4 h-4 text-emerald-600" />} label="Reward system active"
               help="When OFF, no new points are earned on payments and redemption is blocked.">
            <Switch checked={!!s.reward_system_active}
                    onCheckedChange={(v) => setS({ ...s, reward_system_active: v })}
                    data-testid="setting-active" />
          </Row>

          <Row icon={<Percent className="w-4 h-4 text-amber-600" />} label="Earning percentage"
               help="Points awarded per ₹100 paid. Default 4% (₹10,000 ⇒ 400 points).">
            <div className="flex items-center gap-2">
              <Input type="number" min="0" max="100" step="0.5"
                     value={s.reward_earning_percentage}
                     onChange={(e) => setS({ ...s, reward_earning_percentage: e.target.value })}
                     className="w-28 rounded-xl text-right" data-testid="setting-percentage" />
              <span className="text-slate-500">%</span>
            </div>
          </Row>

          <Row icon={<AlarmClockOff className="w-4 h-4 text-rose-600" />} label="Point expiry duration"
               help="Earned points expire after this many months.">
            <div className="flex items-center gap-2">
              <Input type="number" min="1" max="120"
                     value={s.point_expiry_months}
                     onChange={(e) => setS({ ...s, point_expiry_months: e.target.value })}
                     className="w-24 rounded-xl text-right" data-testid="setting-expiry" />
              <span className="text-slate-500">months</span>
            </div>
          </Row>

          <Row icon={<Calculator className="w-4 h-4 text-indigo-600" />} label="Rounding rule"
               help="How fractional points are rounded.">
            <Select value={s.rounding_rule} onValueChange={(v) => setS({ ...s, rounding_rule: v })}>
              <SelectTrigger className="w-40 rounded-xl" data-testid="setting-rounding"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="down">Round down</SelectItem>
                <SelectItem value="nearest">Nearest</SelectItem>
                <SelectItem value="up">Round up</SelectItem>
              </SelectContent>
            </Select>
          </Row>

          <Row icon={<Sliders className="w-4 h-4 text-purple-600" />} label="Allow manual adjustments"
               help="Lets admins add or deduct points from a patient's wallet.">
            <Switch checked={!!s.manual_adjustment_allowed}
                    onCheckedChange={(v) => setS({ ...s, manual_adjustment_allowed: v })}
                    data-testid="setting-manual-allowed" />
          </Row>

          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <Button variant="outline" className="rounded-xl" onClick={runExpiryNow} data-testid="settings-run-expiry-btn">
              Run expiry sweep now
            </Button>
            <Button disabled={saving} onClick={handleSave}
                    className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="settings-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </Card>

        <p className="text-xs text-slate-400 text-center">
          Eden Reward Points are not money. They have no rupee value and cannot be applied to normal bills.
        </p>
      </div>
    </Layout>
  );
}

const Row = ({ icon, label, help, children }) => (
  <div className="flex items-center justify-between gap-4 flex-wrap">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        {icon}
        <Label className="text-sm font-medium text-slate-900">{label}</Label>
      </div>
      {help && <p className="text-xs text-slate-500 ml-6 mt-0.5">{help}</p>}
    </div>
    <div>{children}</div>
  </div>
);
